import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/endpoints';
import AppStore from '../app.store';
import {
  createVoiceMediaRecorder,
  getAudioMediaStream,
  hasGetUserMediaCapability,
  pickMediaRecorderMime,
  pickVoiceRecordingBackend,
  startWavCapture,
  type WavCaptureHandles,
} from '../utils/voiceRecording';

export const SPEECH_TO_TEXT_MAX_MS = 60_000;
/** Таймаут HTTP-запроса распознавания (чуть больше серверного лимита Google STT). */
export const SPEECH_TO_TEXT_FETCH_TIMEOUT_MS = 95_000;

export function formatRecordingMmSs(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export type SpeechToTextContext = 'chat' | 'product_ai' | 'business_onboarding';

export type UseSpeechToTextOptions = {
  /** Куда подставить распознанный текст */
  onTranscribedText: (text: string) => void;
  /** Внешняя блокировка (loading чата, aiLoading и т.д.) */
  isExternallyBusy?: boolean;
  /** Контекст для Gemini-постобработки на сервере */
  speechContext?: SpeechToTextContext;
};

/**
 * Запись голоса и распознавание через тот же endpoint, что и чат помощника (multipart audio → текст).
 */
export function useSpeechToText(options: UseSpeechToTextOptions) {
  const { onTranscribedText, isExternallyBusy = false, speechContext = 'chat' } = options;

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordTimeoutRef = useRef<number | null>(null);
  const wavCaptureRef = useRef<WavCaptureHandles | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const stopRecordingRef = useRef<() => void>(() => undefined);
  const discardVoiceRef = useRef(false);

  const [voiceErr, setVoiceErr] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordElapsedSec, setRecordElapsedSec] = useState(0);
  const [speechLoading, setSpeechLoading] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(
    () => typeof window !== 'undefined' && hasGetUserMediaCapability()
  );

  useEffect(() => {
    const refresh = () => setVoiceSupported(hasGetUserMediaCapability());
    refresh();
    window.addEventListener('resize', refresh);
    window.addEventListener('orientationchange', refresh);
    return () => {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('orientationchange', refresh);
    };
  }, []);

  useEffect(() => {
    if (!isRecording) {
      setRecordElapsedSec(0);
      return;
    }
    const t0 = Date.now();
    setRecordElapsedSec(0);
    const id = window.setInterval(() => {
      setRecordElapsedSec(Math.floor((Date.now() - t0) / 1000));
    }, 400);
    return () => clearInterval(id);
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (recordTimeoutRef.current) {
        clearTimeout(recordTimeoutRef.current);
        recordTimeoutRef.current = null;
      }
      if (wavCaptureRef.current) {
        discardVoiceRef.current = true;
        try {
          wavCaptureRef.current.stop();
        } catch {
          /* noop */
        }
        wavCaptureRef.current = null;
      }
      try {
        mediaRecorderRef.current?.stop();
      } catch {
        /* noop */
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const transcribeBlob = useCallback(
    async (blob: Blob) => {
      setSpeechLoading(true);
      setVoiceErr(null);
      try {
        const token = AppStore.getToken?.();
        if (!token) {
          setVoiceErr('Войдите в систему для голосового ввода');
          return;
        }

        const upload = (accessToken: string) => {
          const fd = new FormData();
          const filename = blob.type.includes('wav') ? 'voice.wav' : 'voice.webm';
          fd.append('audio', blob, filename);
          fd.append('context', speechContext);
          const controller = new AbortController();
          const timeoutId = window.setTimeout(() => controller.abort(), SPEECH_TO_TEXT_FETCH_TIMEOUT_MS);
          return fetch(api.chat_speech_to_text, {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + accessToken },
            body: fd,
            signal: controller.signal,
          }).finally(() => clearTimeout(timeoutId));
        };

        let res = await upload(token);
        if (res.status === 401) {
          const newToken = await AppStore.refreshAccessToken();
          if (newToken) {
            res = await upload(newToken);
          } else {
            setVoiceErr('Сессия истекла');
            return;
          }
        }

        const data = (await res.json().catch(() => ({}))) as { error?: string; text?: string };
        if (!res.ok) {
          if (res.status === 504) {
            setVoiceErr(typeof data.error === 'string' ? data.error : 'Превышено время ожидания распознавания на сервере');
            return;
          }
          setVoiceErr(typeof data.error === 'string' ? data.error : 'Ошибка распознавания речи');
          return;
        }
        const text = (data.text ?? '').trim();
        if (text) {
          onTranscribedText(text);
        } else {
          setVoiceErr('Не удалось распознать речь. Говорите чётче на таджикском, узбекском или русском.');
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          setVoiceErr('Превышено время ожидания распознавания. Проверьте сеть и логи сервера (speech).');
        } else {
          setVoiceErr((e as Error).message);
        }
      } finally {
        setSpeechLoading(false);
      }
    },
    [onTranscribedText, speechContext]
  );

  const stopRecording = useCallback(() => {
    if (recordTimeoutRef.current) {
      clearTimeout(recordTimeoutRef.current);
      recordTimeoutRef.current = null;
    }
    if (wavCaptureRef.current) {
      const h = wavCaptureRef.current;
      wavCaptureRef.current = null;
      h.stop();
      return;
    }
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.stop();
    }
    setIsRecording(false);
  }, []);

  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  const cancelVoiceRecording = useCallback(() => {
    discardVoiceRef.current = true;
    setVoiceErr(null);
    stopRecording();
  }, [stopRecording]);

  const finishVoiceRecording = useCallback(() => {
    discardVoiceRef.current = false;
    stopRecording();
  }, [stopRecording]);

  useEffect(() => {
    if (!isRecording) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelVoiceRecording();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isRecording, cancelVoiceRecording]);

  const startRecording = useCallback(async () => {
    if (isExternallyBusy || speechLoading || isRecording) return;
    discardVoiceRef.current = false;
    setVoiceErr(null);
    const backend = pickVoiceRecordingBackend();
    if (!backend) {
      setVoiceErr('Запись голоса не поддерживается в этом браузере. Нужен HTTPS и актуальный Safari или Chrome.');
      return;
    }
    try {
      const stream = await getAudioMediaStream();
      streamRef.current = stream;

      if (backend === 'wav') {
        const wavHandles = startWavCapture(
          stream,
          (blob) => {
            wavCaptureRef.current = null;
            streamRef.current = null;
            const discard = discardVoiceRef.current;
            discardVoiceRef.current = false;
            setIsRecording(false);
            if (!discard && blob.size > 0) {
              void transcribeBlob(blob);
            }
          },
          (err) => {
            wavCaptureRef.current = null;
            streamRef.current = null;
            setIsRecording(false);
            setVoiceErr(err.message);
          }
        );
        if (!wavHandles) {
          stream.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
          return;
        }
        wavCaptureRef.current = wavHandles;
        setIsRecording(true);
        recordTimeoutRef.current = window.setTimeout(() => {
          discardVoiceRef.current = false;
          stopRecordingRef.current();
        }, SPEECH_TO_TEXT_MAX_MS);
        return;
      }

      const mime = pickMediaRecorderMime();
      if (!mime) {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setVoiceErr('Запись голоса не поддерживается в этом браузере');
        return;
      }

      const rec = createVoiceMediaRecorder(stream, mime);
      mediaRecorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        const discard = discardVoiceRef.current;
        discardVoiceRef.current = false;
        if (!discard && blob.size > 0) {
          void transcribeBlob(blob);
        }
      };
      rec.start();
      setIsRecording(true);
      recordTimeoutRef.current = window.setTimeout(() => {
        discardVoiceRef.current = false;
        stopRecordingRef.current();
      }, SPEECH_TO_TEXT_MAX_MS);
    } catch {
      setVoiceErr('Не удалось получить доступ к микрофону. Разрешите доступ в настройках браузера.');
    }
  }, [isExternallyBusy, speechLoading, isRecording, transcribeBlob]);

  return {
    voiceSupported,
    voiceErr,
    setVoiceErr,
    speechLoading,
    isRecording,
    recordElapsedSec,
    startRecording,
    cancelVoiceRecording,
    finishVoiceRecording,
  };
}
